import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";

/**
 * Generic filter dropdown used across all admin table pages.
 *
 * Props:
 *   value       {string}              – selected option value
 *   onChange    {fn(value)}           – called when an option is selected
 *   options     {Array<{label, value}>} – list of options
 *   placeholder {string}              – label shown when no option is selected (value==="all")
 *   minWidth    {string}              – tailwind min-w class, default "min-w-[130px]"
 *   showCheck   {bool}                – show checkmark on selected item, default true
 */
const FilterDropdown = ({
  value,
  onChange,
  options = [],
  placeholder = "Filter",
  minWidth = "min-w-[130px]",
  showCheck = true,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const selected = options.find((o) => o.value === value);
  const label    = selected && selected.value !== "all" ? selected.label : placeholder;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 px-4 py-2 border border-surface-border rounded-lg text-sm font-medium text-brand-text bg-white hover:bg-surface transition-colors justify-between ${minWidth}`}
      >
        <span className="truncate">{label}</span>
        <ChevronDown
          className={`w-4 h-4 text-brand-muted flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute right-0 mt-1.5 min-w-[160px] bg-white border border-surface-border rounded-xl shadow-lg z-30 py-1 overflow-hidden">
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className="flex items-center justify-between w-full px-4 py-2.5 text-sm text-brand-text hover:bg-surface transition-colors"
            >
              <span>{opt.label}</span>
              {showCheck && value === opt.value && (
                <Check className="w-3.5 h-3.5 text-primary flex-shrink-0 ml-2" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;
